/**
 * ImportFailureRecovery - Premium Product-Aware Import Failure/Recovery Component
 * 
 * Phase: Import Flow Enhancement + Product-Aware Extension
 * 
 * A polished, high-trust recovery UI for when Secure Login Import fails.
 * Displays user-friendly error messages, allows credential correction,
 * and provides clear recovery paths.
 * 
 * PRODUCT-AWARE VARIANTS:
 * - Auto Starter (self-serve): End-user/self-service language
 * - Repair Pro / Agency (staff): Client-management/specialist language
 * 
 * Features:
 * - Error code to user-friendly message mapping
 * - Secure masked credential handling
 * - Provider login action
 * - Retry / Close / Manual HTML upload fallback / Contact Support actions
 * - "Possible Reasons" guidance section
 * - Responsive desktop + mobile layout
 */

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import {
  AlertTriangle,
  Shield,
  ExternalLink,
  Eye,
  EyeOff,
  RefreshCw,
  X,
  Upload,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Key,
  Mail,
  Lock,
  AlertCircle,
  CheckCircle,
  Info,
  Headphones,
  UserCog,
  User,
} from 'lucide-react';
import { Button } from '../ui/button';
import { 
  renderProviderLogo, 
  getProvider, 
  normalizeProviderKey 
} from '../../config/providerRegistry';

// ============================================
// PRODUCT-AWARE COPY CONFIGURATIONS
// ============================================

const PRODUCT_COPY = {
  // Auto Starter / Self-Serve Experience
  autoStarter: {
    title: 'Unable to Import Your Report',
    subtitle: 'Please check your credit monitoring details and try again.',
    quickIntro: 'Make sure your provider, username, password, and security code are correct.',
    credentialSectionTitle: 'Verify Your Login Details',
    providerLabel: 'Your Monitoring Provider',
    emailLabel: 'Your Email or Username',
    passwordLabel: 'Your Password',
    secretLabel: 'Last 4 of SSN or Security Answer',
    secretHint: 'Some providers require the last 4 digits of your SSN or a security answer for verification.',
    retryButton: 'Retry Import',
    providerButton: 'Complete Secure Verification',
    uploadButton: 'Upload Report File Instead',
    supportButton: 'Contact Support',
    supportFooterTitle: 'Still having trouble?',
    supportFooterText: "If you've checked the details above and the issue continues, our support team is here to help.",
    possibleReasons: [
      {
        icon: Lock,
        iconBg: 'bg-red-50',
        iconColor: 'text-red-500',
        title: "Login details don't match",
        description: 'Your username, password, or security code may be incorrect.',
      },
      {
        icon: CreditCard,
        iconBg: 'bg-emerald-50',
        iconColor: 'text-emerald-500',
        title: 'Full report is not available',
        description: 'Some plans only show scores, not the full 3-bureau report needed for import.',
      },
      {
        icon: Shield,
        iconBg: 'bg-amber-50',
        iconColor: 'text-amber-500',
        title: 'Extra verification is required',
        description: 'Your provider may need you to confirm your identity or clear a pop-up first.',
      },
    ],
  },
  
  // Repair Pro / Agency / Staff Experience
  repairPro: {
    title: 'Unable to Import Client Report',
    subtitle: "Please review the client's monitoring details before retrying.",
    quickIntro: "Confirm the client's provider, username, password, and security code are correct.",
    credentialSectionTitle: "Verify Client's Login Details",
    providerLabel: "Client's Monitoring Provider",
    emailLabel: "Client's Email or Username",
    passwordLabel: "Client's Password",
    secretLabel: "Client's Security Answer",
    secretHint: 'Some providers require the last 4 digits of SSN or a security answer for verification.',
    retryButton: 'Retry Import',
    providerButton: 'Complete Secure Verification',
    uploadButton: 'Upload Report File Instead',
    supportButton: 'Contact Support',
    supportFooterTitle: 'Need assistance?',
    supportFooterText: "If the client's details are correct and the issue persists, our support team can help troubleshoot.",
    possibleReasons: [
      {
        icon: UserCog,
        iconBg: 'bg-red-50',
        iconColor: 'text-red-500',
        title: 'Client login details are incorrect',
        description: 'The username, password, or security code may not match.',
      },
      {
        icon: CreditCard,
        iconBg: 'bg-emerald-50',
        iconColor: 'text-emerald-500',
        title: "Client's full report is unavailable",
        description: 'The monitoring account may not include the full 3-bureau report.',
      },
      {
        icon: AlertTriangle,
        iconBg: 'bg-amber-50',
        iconColor: 'text-amber-500',
        title: 'Client account needs attention',
        description: 'Billing, verification, or account setup may need to be completed first.',
      },
    ],
  },
};

// ============================================
// ERROR MAPPING: Backend codes to user-friendly messages (Product-Aware)
// ============================================

const getErrorMessages = (isStaffContext) => ({
  invalid_credentials: {
    title: 'Login Details Incorrect',
    description: isStaffContext
      ? "The client's IdentityIQ username/email or password appears to be incorrect. This is not their FundElite app password."
      : 'IdentityIQ credentials were rejected. Please check your IdentityIQ login, not your FundElite password.',
    suggestion: isStaffContext
      ? "Please verify the client's IdentityIQ monitoring login and try again."
      : 'Please verify your IdentityIQ monitoring login and try again.',
  },
  authentication_failed: {
    title: 'Sign-In Could Not Be Confirmed',
    description: isStaffContext
      ? "We could not confirm the client's CreditHeroScore sign-in. Ask them to retry, or verify the username and password if the provider rejected the login."
      : 'We could not confirm the CreditHeroScore sign-in. Please retry, or verify your username and password if the provider rejected the login.',
    suggestion: isStaffContext
      ? "Please verify the client's monitoring login and try again."
      : 'Please verify your monitoring login and try again.',
  },
  dashboard_not_detected: {
    title: 'Report Dashboard Did Not Load',
    description:
      'We signed in, but CreditHeroScore did not finish loading the report dashboard. Please retry in a few minutes or upload your report file manually.',
    suggestion: isStaffContext
      ? 'This is not a username or password problem. Retry the import or upload the report file.'
      : 'This is not a username or password problem. Retry the import or upload your report file.',
  },
  invalid_secret_answer: {
    title: 'Security Answer Incorrect',
    description: isStaffContext
      ? "The client's last 4 digits of SSN or security answer did not match."
      : 'The last 4 digits of your SSN or security answer did not match.',
    suggestion: isStaffContext
      ? 'Double-check the value with the client and ensure it matches their monitoring provider records.'
      : 'Double-check the value and ensure it matches your monitoring provider records.',
  },
  report_unavailable: {
    title: 'Full Report Not Available',
    description: isStaffContext
      ? "The client's monitoring account does not currently have a full report available."
      : 'Your credit monitoring account does not currently have a full report available.',
    suggestion: isStaffContext
      ? "Please ensure the client's subscription includes the full 3-bureau report."
      : 'Please ensure your subscription includes the full 3-bureau report.',
  },
  provider_subscription_issue: {
    title: 'Account Subscription Issue',
    description: isStaffContext
      ? "There may be a payment or subscription issue with the client's monitoring account."
      : 'There may be a payment or subscription issue with your monitoring account.',
    suggestion: isStaffContext
      ? "Please verify the client's account status is active and in good standing."
      : 'Please verify your account status is active and in good standing.',
  },
  provider_verification_required: {
    title: 'Provider Verification Needed',
    description: isStaffContext
      ? "The monitoring provider is requesting additional verification from the client."
      : 'The monitoring provider is requesting additional verification.',
    suggestion: isStaffContext
      ? 'Please have the client log in directly to their provider to complete any verification steps.'
      : 'Please log in directly to your provider to complete any verification steps.',
  },
  provider_unreachable: {
    title: 'Provider Temporarily Unavailable',
    description: 'We could not connect to the credit monitoring provider at this time.',
    suggestion: 'Please wait a few moments and try again, or upload your report file manually.',
  },
  unsupported_provider_state: {
    title: 'Provider State Not Supported',
    description: 'The monitoring provider returned an unexpected state we cannot process.',
    suggestion: isStaffContext
      ? 'Please have the client log in to their provider directly and try the import again.'
      : 'Please log in to your provider directly and try the import again.',
  },
  provider_billing_action_required: {
    title: 'Secure Verification Required',
    description: isStaffContext
      ? "The client's monitoring account needs a payment or billing update before we can retrieve the latest report."
      : 'Your monitoring account needs an update before we can retrieve the latest report.',
    suggestion: isStaffContext
      ? "Please have the client update the billing/payment method on their monitoring membership, then return here to run the secure import again. You can also upload the report manually."
      : 'Please update the billing/payment method connected to your monitoring membership, then return here and run the secure import again. You can also upload your latest report manually to continue.',
  },
  provider_refresh_not_available_yet: {
    title: 'Latest Report Already Saved',
    description: isStaffContext
      ? "The client's latest monitoring report is already saved. A new refresh is not available yet."
      : 'Your latest monitoring report is already saved. A new refresh is not available yet.',
    suggestion: isStaffContext
      ? "We'll continue tracking this provider. The next refresh will be available soon."
      : "We'll continue tracking your monitoring provider. Check back when a new refresh becomes available.",
  },
  validation_failed: {
    title: 'Import Validation Failed',
    description: 'The import completed but the report data could not be verified.',
    suggestion: isStaffContext
      ? 'Please try the import again or upload the report file manually.'
      : 'Please try again or upload your report file manually.',
  },
  missing_report_id: {
    title: 'Report Not Generated',
    description: 'The import completed but no report was created.',
    suggestion: isStaffContext
      ? 'Please verify credentials and retry, or upload the report manually.'
      : 'Please verify your credentials and retry, or upload the report manually.',
  },
  parser_failed: {
    title: 'Report Processing Failed',
    description: 'The report was retrieved but could not be processed correctly.',
    suggestion: isStaffContext
      ? 'The format may be unsupported. Try uploading the report file manually.'
      : 'The format may be unsupported. Try uploading your report file manually.',
  },
  report_not_saved: {
    title: 'Report Could Not Be Saved',
    description: 'The report was processed but could not be saved to your account.',
    suggestion: 'Please retry the import. If the issue persists, contact support.',
  },
  old_report_detected: {
    title: 'No New Report Found',
    description: 'The import did not produce a new report and only existing data was detected.',
    suggestion: isStaffContext
      ? "Ensure the client's monitoring account has updated data, then retry."
      : 'Ensure your monitoring account has updated data, then retry.',
  },
  unknown_import_failure: {
    title: 'Import Could Not Complete',
    description: 'We encountered an unexpected issue retrieving the credit report.',
    suggestion: isStaffContext
      ? "Please verify the client's credentials and try again, or upload the report file manually."
      : 'Please verify your credentials and try again, or upload your report file manually.',
  },
  status_http_401: {
    title: 'Session Expired',
    description: 'Your session expired. Please sign in again.',
    suggestion: 'Sign in again to continue. The import may still be processing in the background.',
  },
  status_http_403: {
    title: 'Access Restricted',
    description: 'You do not have access to this import. Refresh the page or contact your administrator.',
    suggestion: 'This is an account-access issue, not a problem with the monitoring credentials.',
  },
  status_poll_failed: {
    title: 'Status Updates Unavailable',
    description: 'The import is still processing, but status updates are temporarily unavailable.',
    suggestion: 'Please check back shortly or refresh the page. No credential changes are needed.',
  },
});

// Authorization/session polling errors — never show the credential retry form for these.
const AUTHORIZATION_ERROR_CODES = new Set(['status_http_401', 'status_http_403']);
const CREDENTIAL_RETRY_ERROR_CODES = new Set(['invalid_credentials', 'authentication_failed']);

// ============================================
// PROVIDER URLs for "Complete Secure Verification" action
// Now uses provider config passed from parent, with fallback
// ============================================

const FALLBACK_PROVIDER_URLS = {
  identityiq: 'https://www.identityiq.com/login.aspx',
  credit_hero_score: 'https://www.creditheroscore.com/customer_login.asp',
  smartcredit: 'https://member.smartcredit.com/login',
  myscoreiq: 'https://www.myscoreiq.com/login.aspx',
  myfreescorenow: 'https://www.myfreescorenow.com/login',
  privacyguard: 'https://www.privacyguard.com/login.aspx',
};

const buildAutoStarterCopy = (t) => ({
  title: t('importRecovery.title'),
  subtitle: t('importRecovery.subtitle'),
  quickIntro: t('importRecovery.quickIntro'),
  credentialSectionTitle: t('importRecovery.credentialSectionTitle'),
  providerLabel: t('importRecovery.providerLabel'),
  emailLabel: t('importRecovery.emailLabel'),
  passwordLabel: t('importRecovery.passwordLabel'),
  secretLabel: t('importRecovery.secretLabel'),
  secretHint: t('importRecovery.secretHint'),
  retryButton: t('importRecovery.retryButton'),
  providerButton: t('importRecovery.providerButton'),
  uploadButton: t('importRecovery.uploadButton'),
  supportButton: t('importRecovery.supportButton'),
  supportFooterTitle: t('importRecovery.supportFooterTitle'),
  supportFooterText: t('importRecovery.supportFooterText'),
  possibleReasons: [
    {
      icon: Lock,
      iconBg: 'bg-red-50',
      iconColor: 'text-red-500',
      title: t('importRecovery.reason1Title'),
      description: t('importRecovery.reason1Description'),
    },
    {
      icon: CreditCard,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-500',
      title: t('importRecovery.reason2Title'),
      description: t('importRecovery.reason2Description'),
    },
    {
      icon: Shield,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-500',
      title: t('importRecovery.reason3Title'),
      description: t('importRecovery.reason3Description'),
    },
  ],
});

// ============================================
// MAIN COMPONENT
// ============================================

const ImportFailureRecovery = ({
  isOpen,
  onClose,
  onRetry,
  onManualFileSelected,
  provider,
  errorCode = 'unknown_import_failure',
  initialCredentials = {},
  isStaffContext = false,
  recoveryUploadState = 'idle',
  recoveryUploadError = null,
  onViewCreditReport,
  onUploadAnotherReport,
  theme = null,
  savedReportId = null,
  recoveryMode = 'failure',
}) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const manualFileInputRef = useRef(null);
  
  // State for credential editing
  const [credentials, setCredentials] = useState({
    email: initialCredentials.email || '',
    password: '',
    secretAnswer: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showSecretAnswer, setShowSecretAnswer] = useState(false);
  const [showPossibleReasons, setShowPossibleReasons] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const copy = isStaffContext ? PRODUCT_COPY.repairPro : buildAutoStarterCopy(t);
  const errorMessages = getErrorMessages(isStaffContext);
  const errorInfo = errorMessages[errorCode] || errorMessages.unknown_import_failure;
  const isAuthorizationError = AUTHORIZATION_ERROR_CODES.has(errorCode);
  const showCredentialRetry = CREDENTIAL_RETRY_ERROR_CODES.has(errorCode);

  const effectiveMode = savedReportId || recoveryMode === 'review'
    ? 'review'
    : errorCode === 'provider_refresh_not_available_yet'
      ? 'saved_available'
      : 'failure';

  const modalTitle = effectiveMode === 'review'
    ? 'Report Imported, Review Needed'
    : effectiveMode === 'saved_available'
      ? (errorMessages.provider_refresh_not_available_yet?.title || copy.title)
      : copy.title;

  const modalSubtitle = effectiveMode === 'review'
    ? 'Your report was saved. Open it to review the imported data.'
    : effectiveMode === 'saved_available'
      ? (errorMessages.provider_refresh_not_available_yet?.description || copy.subtitle)
      : copy.subtitle;

  const textPrimary = theme?.text?.primary || undefined;
  const textSecondary = theme?.text?.secondary || undefined;
  const textMuted = theme?.text?.muted || undefined;
  const surfaceStyle = theme
    ? {
        background: theme.card?.bg || '#1A2F2A',
        border: `1px solid ${theme.card?.border || '#2D4A44'}`,
      }
    : undefined;
  const useThemeTokens = Boolean(theme);

  // Handle credential changes (secure - no logging)
  const handleCredentialChange = (field, value) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
  };

  const providerDisplayName = provider?.name || 'IdentityIQ';
  const canRetry =
    Boolean(credentials.email?.trim()) && Boolean(credentials.password);

  // Handle retry with new credentials
  const handleRetry = async () => {
    if (!canRetry) {
      return;
    }

    setIsRetrying(true);
    try {
      await onRetry({
        provider: provider?.id,
        email: credentials.email,
        password: credentials.password,
        secretAnswer: credentials.secretAnswer,
      });
    } catch {
      // Error handling is done by parent
    } finally {
      setIsRetrying(false);
    }
  };

  // Open provider login page - use referral URL if available, else login URL
  const handleOpenProviderSite = () => {
    // Priority: provider.referralUrl > provider.loginUrl > fallback
    const url = provider?.referralUrl 
      || provider?.loginUrl 
      || FALLBACK_PROVIDER_URLS[provider?.id] 
      || FALLBACK_PROVIDER_URLS.identityiq;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Handle contact support
  const handleContactSupport = () => {
    // Navigate to settings or help section - use existing support pattern
    navigate('/settings');
    onClose();
  };

  const handleUploadButtonClick = () => {
    manualFileInputRef.current?.click();
  };

  const handleManualFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file && onManualFileSelected) {
      onManualFileSelected(file);
    }
  };

  const isUploadSuccess = recoveryUploadState === 'success';
  const isUploading = recoveryUploadState === 'uploading';
  const showFailureContent = !isUploadSuccess && !isUploading;

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm ${
        useThemeTokens ? '' : 'bg-stone-900/60'
      }`}
      style={useThemeTokens ? { background: 'rgba(0,0,0,0.55)' } : undefined}
      data-testid="import-failure-recovery"
    >
      <input
        ref={manualFileInputRef}
        type="file"
        accept=".html,.htm"
        className="hidden"
        onChange={handleManualFileChange}
        data-testid="recovery-manual-upload-input"
      />
      <div 
        className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl animate-fade-in ${
          useThemeTokens ? '' : 'bg-white border border-stone-200'
        }`}
        style={surfaceStyle}
        data-testid="recovery-modal"
      >
        {/* Header */}
        <div
          className={`sticky top-0 rounded-t-2xl border-b px-6 py-5 z-10 ${
            useThemeTokens ? '' : 'bg-white border-slate-100'
          }`}
          style={useThemeTokens ? {
            background: theme?.surface?.s1 || theme?.card?.bg,
            borderColor: theme?.border?.b2 || theme?.card?.border,
          } : undefined}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  isUploadSuccess
                    ? 'bg-emerald-50 dark:bg-emerald-500/15'
                    : 'bg-amber-50 dark:bg-amber-500/15'
                }`}
              >
                {isUploadSuccess ? (
                  <CheckCircle className="text-emerald-600 dark:text-emerald-400" size={24} />
                ) : (
                  <AlertTriangle className="text-amber-500 dark:text-amber-400" size={24} />
                )}
              </div>
              <div>
                <h2
                  className={`text-xl font-semibold ${useThemeTokens ? '' : 'text-stone-900'}`}
                  style={textPrimary ? { color: textPrimary } : undefined}
                  data-testid="modal-title"
                >
                  {isUploadSuccess
                    ? t('importRecovery.uploadSuccessTitle')
                    : isUploading
                      ? t('importRecovery.uploadingTitle')
                      : modalTitle}
                </h2>
                <p
                  className={`text-sm mt-0.5 ${useThemeTokens ? '' : 'text-slate-500'}`}
                  style={textMuted ? { color: textMuted } : undefined}
                >
                  {isUploadSuccess
                    ? t('importRecovery.uploadSuccessSubtitle')
                    : isUploading
                      ? t('importRecovery.uploadingSubtitle')
                      : modalSubtitle}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-stone-800 transition-colors"
              data-testid="close-recovery"
            >
              <X size={20} className="text-slate-400" />
            </button>
          </div>
        </div>

        {isUploading && (
          <div className="px-6 py-10 flex flex-col items-center gap-4" data-testid="recovery-uploading">
            <RefreshCw className="animate-spin text-emerald-600 dark:text-emerald-400" size={32} />
            <p className="text-sm text-slate-600 dark:text-slate-300 text-center">
              {t('importRecovery.uploadingSubtitle')}
            </p>
          </div>
        )}

        {isUploadSuccess && (
          <div className="px-6 py-8 space-y-6" data-testid="recovery-upload-success">
            <div className="p-4 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 rounded-xl">
              <p className="text-sm text-emerald-800 dark:text-emerald-200">
                {t('importRecovery.uploadSuccessSubtitle')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                onClick={onViewCreditReport}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-medium"
                data-testid="view-credit-report-after-upload"
              >
                <CheckCircle className="mr-2" size={18} />
                {t('importRecovery.viewCreditReport')}
              </Button>
              <Button
                type="button"
                onClick={onUploadAnotherReport}
                variant="outline"
                className="flex-1 py-3 rounded-xl font-medium dark:border-stone-600 dark:text-stone-200"
                data-testid="upload-another-report"
              >
                <Upload className="mr-2" size={18} />
                {t('importRecovery.uploadAnotherReport')}
              </Button>
            </div>
          </div>
        )}

        {showFailureContent && (
        <>
        {/* Content */}
        <div className="px-6 py-5 space-y-6">
          {effectiveMode !== 'review' && !isAuthorizationError && (
          <p
            className={`text-sm ${useThemeTokens ? '' : 'text-slate-600'}`}
            style={textSecondary ? { color: textSecondary } : undefined}
            data-testid="quick-intro"
          >
            {copy.quickIntro}
          </p>
          )}

          {recoveryUploadState === 'error' && recoveryUploadError && (
            <div
              className="p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl"
              data-testid="manual-upload-error"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-red-800 dark:text-red-200">
                    {t('importRecovery.manualUploadErrorTitle')}
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">{recoveryUploadError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Error Info Card — credential import only */}
          <div className="p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl" data-testid="error-info">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-200">{errorInfo.title}</p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">{errorInfo.description}</p>
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1.5">
                  <Info size={14} />
                  {errorInfo.suggestion}
                </p>
              </div>
            </div>
          </div>

          {/* Login Recovery Section — hidden for authorization/session errors */}
          {effectiveMode === 'failure' && !isAuthorizationError && showCredentialRetry && (
          <div className="space-y-4" data-testid="credential-section">
            <div className="flex items-center gap-2">
              <Shield className="text-emerald-600" size={18} />
              <h3
                className={`font-semibold ${useThemeTokens ? '' : 'text-stone-900'}`}
                style={textPrimary ? { color: textPrimary } : undefined}
              >
                {copy.credentialSectionTitle}
              </h3>
            </div>

            {/* Provider Display - Use centralized SVG logo from provider registry */}
            <div
              className={`p-3 rounded-xl border ${
                useThemeTokens ? '' : 'bg-slate-50 border-slate-200'
              }`}
              style={useThemeTokens ? {
                background: theme?.surface?.s2 || 'rgba(255,255,255,0.04)',
                borderColor: theme?.border?.b2 || theme?.card?.border,
              } : undefined}
            >
              <div className="flex items-center gap-3">
                {/* Check if provider exists in registry, use centralized logo */}
                {getProvider(provider?.id || provider?.provider_key) ? (
                  <div className="w-28 h-10 flex items-center justify-start">
                    {renderProviderLogo(provider?.id || provider?.provider_key, 'sm')}
                  </div>
                ) : provider?.logo ? (
                  typeof provider.logo === 'function' ? (
                    <provider.logo className="shadow-sm" />
                  ) : (
                    <span className="text-2xl">{provider.logo}</span>
                  )
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Shield className="text-emerald-600" size={24} />
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">{copy.providerLabel}</p>
                  <p className="font-medium text-stone-900">{provider?.name || 'Credit Monitoring Service'}</p>
                </div>
              </div>
            </div>

            {/* Email/Username Field */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {copy.emailLabel}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="email"
                  value={credentials.email}
                  onChange={(e) => handleCredentialChange('email', e.target.value)}
                  placeholder={isStaffContext ? "Enter client's email or username" : "Enter your email or username"}
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  data-testid="credential-email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {copy.passwordLabel}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={(e) => handleCredentialChange('password', e.target.value)}
                  placeholder={isStaffContext ? "Enter client's password" : "Enter your password"}
                  className="w-full pl-10 pr-12 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  data-testid="credential-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
                >
                  {showPassword ? (
                    <EyeOff size={18} className="text-slate-400" />
                  ) : (
                    <Eye size={18} className="text-slate-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Secret Answer / Last 4 SSN Field */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {copy.secretLabel}
                <span className="text-slate-400 font-normal ml-1">(if required)</span>
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type={showSecretAnswer ? 'text' : 'password'}
                  value={credentials.secretAnswer}
                  onChange={(e) => handleCredentialChange('secretAnswer', e.target.value)}
                  placeholder="Enter if provider requires it"
                  className="w-full pl-10 pr-12 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  data-testid="credential-secret"
                />
                <button
                  type="button"
                  onClick={() => setShowSecretAnswer(!showSecretAnswer)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
                >
                  {showSecretAnswer ? (
                    <EyeOff size={18} className="text-slate-400" />
                  ) : (
                    <Eye size={18} className="text-slate-400" />
                  )}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1.5">
                {copy.secretHint}
              </p>
            </div>

            {/* Complete Secure Verification Button */}
            <button
              type="button"
              onClick={handleOpenProviderSite}
              className="w-full flex items-center justify-center gap-2 py-3 border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
              data-testid="complete-secure-verification"
            >
              <ExternalLink size={18} />
              <span className="font-medium">{copy.providerButton}</span>
            </button>
          </div>
          )}

          {effectiveMode === 'failure' && !isAuthorizationError && showCredentialRetry && (
          <>
          <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="possible-reasons">
            <button
              type="button"
              onClick={() => setShowPossibleReasons(!showPossibleReasons)}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <HelpCircle className="text-slate-400" size={18} />
                <span className="font-medium text-stone-900">{t('importRecovery.possibleReasons')}</span>
              </div>
              {showPossibleReasons ? (
                <ChevronUp className="text-slate-400" size={20} />
              ) : (
                <ChevronDown className="text-slate-400" size={20} />
              )}
            </button>

            {showPossibleReasons && (
              <div className="px-4 pb-4 space-y-4 border-t border-slate-100 pt-4">
                {copy.possibleReasons.map((reason, index) => (
                  <div key={index} className="flex gap-3">
                    <div className={`w-8 h-8 rounded-lg ${reason.iconBg} flex items-center justify-center flex-shrink-0`}>
                      <reason.icon className={reason.iconColor} size={16} />
                    </div>
                    <div>
                      <p className="font-medium text-stone-800 text-sm">{reason.title}</p>
                      <p className="text-sm text-slate-500 mt-0.5">{reason.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Support Footer */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100" data-testid="support-footer">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Headphones className="text-emerald-500" size={16} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-stone-800 text-sm">{copy.supportFooterTitle}</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  {copy.supportFooterText}
                </p>
                <button 
                  type="button"
                  onClick={handleContactSupport}
                  className="inline-flex items-center gap-1.5 mt-2 text-emerald-600 hover:text-emerald-700 font-medium text-sm"
                  data-testid="contact-support-link"
                >
                  <Headphones size={14} />
                  {copy.supportButton}
                </button>
              </div>
            </div>
          </div>
          </>
          )}
        </div>

        {/* Footer Actions */}
        <div
          className={`sticky bottom-0 rounded-b-2xl border-t px-6 py-4 ${
            useThemeTokens ? '' : 'bg-white border-slate-100'
          }`}
          style={useThemeTokens ? {
            background: theme?.surface?.s1 || theme?.card?.bg,
            borderColor: theme?.border?.b2 || theme?.card?.border,
          } : undefined}
        >
          {isAuthorizationError ? (
            <div className="flex flex-col sm:flex-row gap-3" data-testid="authorization-error-actions">
              <Button
                type="button"
                onClick={() => window.location.reload()}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-medium"
                data-testid="refresh-page-btn"
              >
                <RefreshCw className="mr-2" size={18} />
                Refresh Page
              </Button>
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="flex-1 py-3 rounded-xl font-medium"
                style={useThemeTokens ? {
                  borderColor: theme?.border?.b2,
                  color: textPrimary,
                } : undefined}
                data-testid="close-authorization-error"
              >
                <X className="mr-2" size={18} />
                Close
              </Button>
            </div>
          ) : (effectiveMode === 'review' || effectiveMode === 'saved_available') ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                onClick={onViewCreditReport}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-medium"
                data-testid="view-credit-report-recovery"
              >
                <CheckCircle className="mr-2" size={18} />
                View Credit Report
              </Button>
              <Button
                type="button"
                onClick={handleUploadButtonClick}
                variant="outline"
                className="flex-1 py-3 rounded-xl font-medium"
                style={useThemeTokens ? {
                  borderColor: theme?.border?.b2,
                  color: textPrimary,
                } : undefined}
                data-testid="upload-html-instead"
              >
                <Upload className="mr-2" size={18} />
                {copy.uploadButton}
              </Button>
            </div>
          ) : (
          <>
          {!canRetry && !isRetrying && showCredentialRetry && (
            <p
              className={`text-sm mb-3 ${useThemeTokens ? '' : 'text-slate-600'}`}
              style={textMuted ? { color: textMuted } : undefined}
              data-testid="retry-credentials-hint"
            >
              Please enter your {providerDisplayName} email/username and password to retry.
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              onClick={handleUploadButtonClick}
              variant="outline"
              className="flex-1 py-3 rounded-xl font-medium order-2 sm:order-1"
              style={useThemeTokens ? {
                borderColor: theme?.border?.b2,
                color: textPrimary,
              } : undefined}
              data-testid="upload-html-instead"
            >
              <Upload className="mr-2" size={18} />
              {copy.uploadButton}
            </Button>
            {showCredentialRetry && (
            <Button
              type="button"
              onClick={handleRetry}
              disabled={isRetrying || !credentials.email || !credentials.password}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-medium order-1 sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="retry-import"
            >
              {isRetrying ? (
                <>
                  <RefreshCw className="animate-spin mr-2" size={18} />
                  Retrying...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2" size={18} />
                  {copy.retryButton}
                </>
              )}
            </Button>
            )}
          </div>
          </>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default ImportFailureRecovery;
